export type PresetStatus = "active" | "disabled";

export type CompatibleRole = "owner" | "supervisor" | "member" | "viewer";

export type PresetContext = "master" | "workspace" | "vendor";

export type APIPermissionProfilePresetStatus = PresetStatus;

export type APIPermissionProfilePresetUsageSummaryType = Partial<{
  plans_count: number;
  default_assignments_count: number;
}>;

export type APIPermissionProfilePresetResponseType = Partial<{
  id: number;
  url: string;
  preset_code: string;
  name: string;
  description: string;
  compatible_roles: CompatibleRole[];
  contexts: PresetContext[];
  capabilities: string[];
  status: PresetStatus;
  version: number;
  is_system_preset: boolean;
  usage_summary: APIPermissionProfilePresetUsageSummaryType;
  created_at: string;
  updated_at: string;
}>;

export type APIPermissionProfilePresetRequestType = Partial<{
  preset_code: string;
  name: string;
  description: string;
  compatible_roles: CompatibleRole[];
  contexts: PresetContext[];
  capabilities: string[];
}>;

export type APIPermissionProfilePresetDuplicateRequestType = {
  preset_code: string;
  name: string;
};

// Backend devuelve errores por campo (no árbol DRF).
// Ej: { "capabilities": [{ "code": "invalid_capability", "message": "..." }] }
export type APIPermissionProfilePresetValidationErrorItem = {
  code: string;
  message: string;
};

export type APIPermissionProfilePresetValidationErrorType = Partial<
  Record<string, APIPermissionProfilePresetValidationErrorItem[]>
>;

export type APIPermissionProfilePresetUsagePlanType = Partial<{
  plan_code: string;
  name: string;
  status: string;
  current_version: number;
}>;

export type APIPermissionProfilePresetUsageResponseType = Partial<{
  plans_using_preset: APIPermissionProfilePresetUsagePlanType[];
  default_assignments_count: number;
}>;

export interface IAPIPermissionProfilePreset {
  getData(): APIPermissionProfilePresetResponseType;
  isDataValid(): boolean;
  toRequestModel(): APIPermissionProfilePresetRequestType;
}

export class APIPermissionProfilePreset implements IAPIPermissionProfilePreset {
  readonly data: APIPermissionProfilePresetResponseType;

  constructor(data: APIPermissionProfilePresetResponseType) {
    this.data = data;
  }

  getData(): APIPermissionProfilePresetResponseType {
    return this.data;
  }

  isDataValid(): boolean {
    return (
      !!this.data.preset_code &&
      !!this.data.name &&
      (this.data.capabilities?.length ?? 0) > 0
    );
  }

  toRequestModel(): APIPermissionProfilePresetRequestType {
    if (this.isDataValid()) {
      return {
        preset_code: this.data.preset_code!.trim(),
        name: this.data.name!.trim(),
        description: this.data.description ?? "",
        compatible_roles: this.data.compatible_roles ?? [],
        contexts: this.data.contexts ?? [],
        capabilities: this.data.capabilities ?? [],
      };
    }
    throw new Error(
      "Invalid data: preset_code, name and capabilities are required",
    );
  }
}
